import React, { useEffect, useState } from 'react'
import { Search, Plus, Download } from 'lucide-react'
import { Input } from '@/components/ui_custom/input'
import { user_role } from '@/entities/user'
import { api } from '@/lib/axios'
import { useAuthStore } from '@/store/auth'
import { HTTP_STATUS_CODE } from '@/constants/http'
import { toast } from '@/hooks/use-toast'
import { showConfirm } from '@/lib/alerts'
import { useLoader } from '@/contexts/LoaderContext'
import { PageHeader } from '@/components/common/page-header'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card } from '@/components/cards/card'
import { ExamLibraryList } from '@/components/tools-list/exam-library-list'
import { CreateExamLibraryDialog } from '@/components/dialogs/create-exam-library-dialog'
import { ExamLibraryProps, formatted_exam_library_profile_types } from '@/entities/exam-library'
import { Button } from '@/components/ui/button'
import { CustomDialog } from '@/components/dialogs/custom-dialog'
import { handleDownloadFile } from '@/utils/download-util'
import { FileViewer } from '@/components/dialogs/file-preview'
import { useMediaQuery } from '@/hooks/use-media-query'
import { cn } from '@/lib/utils'

const loc = '@/pages/ExamLibrary'

export default function ExamLibrary() {
  const { user } = useAuthStore()
  const { showLoader, hideLoader } = useLoader()
  const isMdAndDown = useMediaQuery('(max-width:1123px)')
  const [searchTerm, setSearchTerm] = useState('')
  const [data, setData] = useState([])
  const [selectedModel, setSelectedModel] = useState<ExamLibraryProps>({})
  const [isCreateModelOpen, setIsCreateModelOpen] = useState<boolean>(false)
  const tabs: { id: string; label: string }[] = Object.entries(formatted_exam_library_profile_types).map(([key, value]) => ({
    id: key,
    label: value
  }))
  const [currentTab, setCurrentTab] = useState<{ id: string; label: string }>(tabs[0])
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)

  const _handleDelete = async (ref: string) => {
    try {
      const { isConfirmed } = await showConfirm('Deseja mesmo deletar ?')
      if (!isConfirmed) return

      showLoader()

      const response = await api.delete('/', {
        data: {
          action: 'exam-library',
          ref,
          userRef: user?.ref
        }
      })

      if (response.data?.statusCode === HTTP_STATUS_CODE.OK) {
        toast({ variant: 'success', title: 'Sucesso', description: 'Exame deletado com sucesso.' })
        await _fetchData(true)
      } else toast({ variant: 'destructive', title: 'Ops ...', description: response.data?.statusMessage || 'Não foi possível deletar o exame.' })
    } catch (err) {
      toast({ variant: 'destructive', title: 'Ops ...', description: 'Não foi possível deletar o exame.' })
      console.error(`Unhandled error at ${loc}._handleDelete. Details: ${err}`)
    } finally {
      hideLoader()
    }
  }

  const _fetchData = async (refetch: boolean) => {
    try {
      if (!refetch && data.length) return

      showLoader()

      const response = await api.get('/', {
        params: {
          action: 'exam-library',
          userRef: user?.ref
        }
      })

      if (response.data.statusCode == HTTP_STATUS_CODE.OK) {
        setData(response.data.body)
      } else {
        toast({
          variant: 'destructive',
          title: 'Ops ...',
          description: response?.data?.statusMessage || 'Erro ao enviar mensagem!'
        })
      }
    } catch (err) {
      console.error(err)
    } finally {
      hideLoader()
    }
  }

  const _handlePreview = (item: ExamLibraryProps) => {
    setSelectedModel(item)
    setIsPreviewOpen(true)
  }

  useEffect(() => {
    _fetchData(false)
  }, [])

  return (
    <>
      <div className="mx-auto">
        <PageHeader title="Biblioteca de Exames" description="Sugestões de exames a solicitar conforme o perfil clínico" />

        <Card
          title="Exames Laboratoriais"
          description="Consulte os exames laboratoriais para cada perfil de paciente"
          className="mt-4"
          headerButton={
            (user?.role == user_role.ADMIN || user?.role == user_role.MODERATOR) && (
              <Button
                className={cn('flex items-center rounded-2xl p-2 bg-primary hover:bg-primary/60 dark:text-white text-white text-sm font-medium', isMdAndDown && 'w-full')}
                onClick={() => {
                  setSelectedModel({} as ExamLibraryProps)
                  setIsCreateModelOpen(true)
                }}
              >
                Adicionar
                <Plus className="h-4 w-4 ml-1" />
              </Button>
            )
          }
        >
          <Input
            type="text"
            leftIcon={<Search className="h-5 w-5 dark:text-white text-gray-400" />}
            placeholder="Buscar exames..."
            className="block w-full pl-10 pr-3 mt-5 py-2 border text-black border-gray-300 rounded-md leading-5 placeholder:text-gray-400 dark:placeholder:text-gray-100 focus:outline-none  sm:text-sm"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />

          <Tabs className="flex flex-col items-center justify-start min-w-full overflow-y-auto overflow-x-hidden mt-5" value={currentTab.id} defaultValue={tabs[0].id} onValueChange={tabId => setCurrentTab(tabs.find(t => t?.id == tabId) as { id: string; label: string })}>
            <TabsList>
              {tabs.map((tab, i) => (
                <TabsTrigger key={i} value={tab.id}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

            <div className="flex-1 w-full pt-4 " onKeyDown={evt => evt.key === 'Enter' && evt?.preventDefault()}>
              {tabs.map((tab, i) => (
                <TabsContent key={i} value={tab.id}>
                  <ExamLibraryList
                    type={tab.id}
                    data={data}
                    onCreate={(ref: string) => {
                      setSelectedModel(data.find(d => d.ref == ref) as ExamLibraryProps)
                      setIsCreateModelOpen(true)
                    }}
                    onDelete={_handleDelete}
                    onPreview={_handlePreview}
                  />
                </TabsContent>
              ))}
            </div>
          </Tabs>
        </Card>
      </div>

      {isCreateModelOpen && (
        <CreateExamLibraryDialog
          open={isCreateModelOpen}
          examLibraryProp={selectedModel}
          cb={() => {
            _fetchData(true)
            setSelectedModel({} as ExamLibraryProps)
            setIsCreateModelOpen(false)
          }}
        />
      )}

      {isPreviewOpen && (
        <CustomDialog
          open={isPreviewOpen}
          persistent={true}
          title="Detalhes do Modelo de Exame"
          confirmationBeforeClose={false}
          onClose={() => {
            setIsPreviewOpen(false)
          }}
          className="min-w-[1000px]"
        >
          <div className="flex flex-row gap-y-2 justify-between">
            <div className="flex flex-col gap-y-2">
              <h1 className="text-lg text-gray-900 dark:text-white">{selectedModel?.title}</h1>
              <p className="text-md text-gray-600 dark:text-gray-400">{selectedModel?.description}</p>
            </div>
            <div className="flex flex-col gap-y-2 mt-5">
              {!isMdAndDown &&
                selectedModel?.paths?.map((path, index) => (
                  <Button variant="outline" key={index} className="flex items-center rounded-2xl p-2  dark:text-white text-primary text-sm font-medium" onClick={() => handleDownloadFile(path, selectedModel.title || '')} disabled={!path?.length}>
                    Baixar Modelo {selectedModel?.title}
                    <Download className="h-4 w-4 ml-1" />
                  </Button>
                ))}
            </div>
          </div>
          <div className="flex flex-col gap-y-2">
            <div className="flex flex-col items-center justify-center pb-3">
              <FileViewer url={selectedModel?.paths[0] || ''} />
            </div>
            {isMdAndDown &&
              selectedModel?.paths?.map((path, index) => (
                <Button variant="outline" key={index} className="flex items-center rounded-2xl p-2  dark:text-white text-primary text-sm font-medium" onClick={() => handleDownloadFile(path, selectedModel.title || '')}>
                  Baixar Modelo {selectedModel?.title}
                  <Download className="h-4 w-4 ml-1" />
                </Button>
              ))}
          </div>
        </CustomDialog>
      )}
    </>
  )
}
